/**
 * Field definitions per event type. Drives the Add Event drawer.
 * Keys are used in form state; primary date key is used for Event.date.
 */

export type FieldInputType =
  | 'text'
  | 'number'
  | 'date'
  | 'datetime'
  | 'time'
  | 'select'
  | 'checkbox'
  | 'url';

/** Drawer groups. Fields without a section render at the top (e.g. Title). */
export type FieldSection = 'booking' | 'when' | 'details' | 'notes';

export interface FieldDef {
  key: string;
  label: string;
  inputType: FieldInputType;
  required: boolean;
  options?: { value: string; label: string }[];
  /** Take one of two grid columns instead of the full row. */
  half?: boolean;
  section?: FieldSection;
  placeholder?: string;
}

export type LogicalEventType =
  | 'Flight'
  | 'Train'
  | 'Bus'
  | 'Ferry'
  | 'Car Rental'
  | 'Road Trip'
  | 'Stay'
  | 'Restaurant'
  | 'Reservation'
  | 'Activity / Event'
  | 'Idea / Open Plan';

/** Primary date key for this type (used for Event.date). */
export const PRIMARY_DATE_KEY: Record<LogicalEventType, string> = {
  Flight: 'startDateTime',
  Train: 'startDateTime',
  Bus: 'startDateTime',
  Ferry: 'startDateTime',
  'Car Rental': 'pickupDateTime',
  'Road Trip': 'startDateTime',
  Stay: 'checkinDate',
  Restaurant: 'dateTime',
  Reservation: 'dateTime',
  'Activity / Event': 'dateTime',
  'Idea / Open Plan': 'approximateDate',
};

/** End date key for multi-day events (used for Event.endDate). */
export const END_DATE_KEY: Partial<Record<LogicalEventType, string>> = {
  Flight: 'endDateTime',
  Train: 'endDateTime',
  Bus: 'endDateTime',
  Ferry: 'endDateTime',
  'Car Rental': 'dropoffDateTime',
  'Road Trip': 'endDateTime',
  Stay: 'checkoutDate',
};

const title = (placeholder: string): FieldDef => ({
  key: 'title',
  label: 'Title',
  inputType: 'text',
  required: true,
  placeholder,
});

const departArrive: FieldDef[] = [
  { key: 'startDateTime', label: 'Departs', inputType: 'datetime', required: true, section: 'when' },
  { key: 'endDateTime', label: 'Arrives', inputType: 'datetime', required: true, section: 'when' },
];

const status: FieldDef = { key: 'status', label: 'Status', inputType: 'select', required: true, section: 'booking', half: true };
const confirmation: FieldDef = { key: 'confirmationNumber', label: 'Confirmation #', inputType: 'text', required: false, section: 'booking', half: true };
const notes: FieldDef = { key: 'notes', label: 'Notes', inputType: 'text', required: false, section: 'notes' };

const ticketTypeOptions = [
  { value: '', label: '—' },
  { value: 'general', label: 'General' },
  { value: 'vip', label: 'VIP' },
  { value: 'ga', label: 'GA' },
  { value: 'reserved', label: 'Reserved' },
  { value: 'other', label: 'Other' },
];

const vehicleTypeOptions = [
  { value: '', label: '—' },
  { value: 'economy', label: 'Economy' },
  { value: 'compact', label: 'Compact' },
  { value: 'midsize', label: 'Midsize' },
  { value: 'fullsize', label: 'Full size' },
  { value: 'suv', label: 'SUV' },
  { value: 'minivan', label: 'Minivan' },
  { value: 'other', label: 'Other' },
];

export const EVENT_FIELD_CONFIG: Record<LogicalEventType, FieldDef[]> = {
  Flight: [
    title('e.g. Flight to Lisbon'),
    ...departArrive,
    { key: 'departureAirport', label: 'From', inputType: 'text', required: false, section: 'details', half: true, placeholder: 'JFK' },
    { key: 'arrivalAirport', label: 'To', inputType: 'text', required: false, section: 'details', half: true, placeholder: 'LIS' },
    { key: 'airline', label: 'Airline', inputType: 'text', required: false, section: 'details', half: true },
    { key: 'flightNumber', label: 'Flight #', inputType: 'text', required: false, section: 'details', half: true },
    { key: 'seat', label: 'Seat', inputType: 'text', required: false, section: 'details', half: true },
    { key: 'gate', label: 'Gate', inputType: 'text', required: false, section: 'details', half: true },
    { key: 'boardingTime', label: 'Boarding time', inputType: 'time', required: false, section: 'details', half: true },
    { key: 'checkedBags', label: 'Checked bags', inputType: 'number', required: false, section: 'details', half: true },
    status,
    confirmation,
    notes,
  ],
  Train: [
    title('e.g. Train to Porto'),
    ...departArrive,
    { key: 'departureStation', label: 'From station', inputType: 'text', required: false, section: 'details', half: true },
    { key: 'arrivalStation', label: 'To station', inputType: 'text', required: false, section: 'details', half: true },
    { key: 'trainOperator', label: 'Operator', inputType: 'text', required: false, section: 'details', half: true },
    { key: 'trainNumber', label: 'Train #', inputType: 'text', required: false, section: 'details', half: true },
    { key: 'carNumber', label: 'Car #', inputType: 'text', required: false, section: 'details', half: true },
    { key: 'seat', label: 'Seat', inputType: 'text', required: false, section: 'details', half: true },
    status,
    confirmation,
    notes,
  ],
  Bus: [
    title('e.g. Bus to Sintra'),
    ...departArrive,
    { key: 'departureStop', label: 'From stop', inputType: 'text', required: false, section: 'details', half: true },
    { key: 'arrivalStop', label: 'To stop', inputType: 'text', required: false, section: 'details', half: true },
    { key: 'busCompany', label: 'Bus company', inputType: 'text', required: false, section: 'details', half: true },
    { key: 'routeNumber', label: 'Route / bus #', inputType: 'text', required: false, section: 'details', half: true },
    { key: 'seat', label: 'Seat', inputType: 'text', required: false, section: 'details', half: true },
    status,
    confirmation,
    notes,
  ],
  Ferry: [
    title('e.g. Ferry to Naxos'),
    ...departArrive,
    { key: 'departurePort', label: 'From port', inputType: 'text', required: false, section: 'details', half: true },
    { key: 'arrivalPort', label: 'To port', inputType: 'text', required: false, section: 'details', half: true },
    { key: 'ferryLine', label: 'Ferry line', inputType: 'text', required: false, section: 'details', half: true },
    { key: 'vessel', label: 'Sailing / vessel', inputType: 'text', required: false, section: 'details', half: true },
    { key: 'cabinSeat', label: 'Cabin / seat', inputType: 'text', required: false, section: 'details', half: true },
    { key: 'vehicleOnBoard', label: 'Vehicle on board', inputType: 'checkbox', required: false, section: 'details', half: true },
    status,
    confirmation,
    notes,
  ],
  'Car Rental': [
    title('e.g. Rental car in Faro'),
    { key: 'pickupDateTime', label: 'Pickup', inputType: 'datetime', required: true, section: 'when' },
    { key: 'dropoffDateTime', label: 'Drop-off', inputType: 'datetime', required: true, section: 'when' },
    { key: 'pickupLocation', label: 'Pickup location', inputType: 'text', required: false, section: 'details', half: true },
    { key: 'dropoffLocation', label: 'Drop-off location', inputType: 'text', required: false, section: 'details', half: true },
    { key: 'rentalCompany', label: 'Rental company', inputType: 'text', required: false, section: 'details', half: true },
    { key: 'vehicleType', label: 'Vehicle type', inputType: 'select', required: false, section: 'details', half: true, options: vehicleTypeOptions },
    { key: 'insuranceAdded', label: 'Insurance added', inputType: 'checkbox', required: false, section: 'details' },
    status,
    confirmation,
    notes,
  ],
  'Road Trip': [
    title('e.g. Drive down the Algarve coast'),
    { key: 'startDateTime', label: 'Start', inputType: 'datetime', required: true, section: 'when' },
    { key: 'endDateTime', label: 'End', inputType: 'datetime', required: true, section: 'when' },
    { key: 'fromLocation', label: 'From', inputType: 'text', required: false, section: 'details', half: true },
    { key: 'toLocation', label: 'To', inputType: 'text', required: false, section: 'details', half: true },
    { key: 'plannedStops', label: 'Planned stops', inputType: 'text', required: false, section: 'details' },
    status,
    confirmation,
    notes,
  ],
  Stay: [
    title('e.g. Hotel in Alfama'),
    { key: 'checkinDate', label: 'Check-in', inputType: 'date', required: true, section: 'when', half: true },
    { key: 'checkoutDate', label: 'Check-out', inputType: 'date', required: true, section: 'when', half: true },
    { key: 'propertyName', label: 'Property name', inputType: 'text', required: false, section: 'details' },
    { key: 'address', label: 'Address', inputType: 'text', required: false, section: 'details' },
    { key: 'hostContact', label: 'Host / contact', inputType: 'text', required: false, section: 'details', half: true },
    { key: 'checkinInstructions', label: 'Check-in instructions', inputType: 'text', required: false, section: 'details', half: true },
    { key: 'totalCost', label: 'Total cost', inputType: 'number', required: false, section: 'details', half: true },
    { key: 'paid', label: 'Paid', inputType: 'checkbox', required: false, section: 'details', half: true },
    status,
    confirmation,
    notes,
  ],
  Restaurant: [
    title('e.g. Dinner at Taberna'),
    { key: 'dateTime', label: 'Date & time', inputType: 'datetime', required: true, section: 'when' },
    { key: 'restaurantName', label: 'Restaurant', inputType: 'text', required: false, section: 'details', half: true },
    { key: 'partySize', label: 'Party size', inputType: 'number', required: false, section: 'details', half: true },
    { key: 'address', label: 'Address', inputType: 'text', required: false, section: 'details' },
    { key: 'specialRequests', label: 'Special requests', inputType: 'text', required: false, section: 'details' },
    status,
    confirmation,
    notes,
  ],
  Reservation: [
    title('e.g. Spa afternoon'),
    { key: 'dateTime', label: 'Date & time', inputType: 'datetime', required: true, section: 'when' },
    { key: 'venue', label: 'Venue / provider', inputType: 'text', required: false, section: 'details' },
    { key: 'address', label: 'Address', inputType: 'text', required: false, section: 'details' },
    { key: 'guests', label: 'Guests', inputType: 'number', required: false, section: 'details', half: true },
    { key: 'cost', label: 'Cost', inputType: 'number', required: false, section: 'details', half: true },
    status,
    confirmation,
    notes,
  ],
  'Activity / Event': [
    title('e.g. Fado show'),
    { key: 'dateTime', label: 'Date & time', inputType: 'datetime', required: true, section: 'when' },
    { key: 'location', label: 'Location', inputType: 'text', required: false, section: 'details' },
    { key: 'ticketType', label: 'Ticket type', inputType: 'select', required: false, section: 'details', half: true, options: ticketTypeOptions },
    { key: 'cost', label: 'Cost', inputType: 'number', required: false, section: 'details', half: true },
    { key: 'paid', label: 'Paid', inputType: 'checkbox', required: false, section: 'details' },
    status,
    confirmation,
    notes,
  ],
  'Idea / Open Plan': [
    title('Add a title'),
    { key: 'approximateDate', label: 'Approximate date', inputType: 'date', required: false, section: 'when', half: true },
    { key: 'link', label: 'Link', inputType: 'url', required: false, section: 'details' },
    status,
    notes,
  ],
};
